import React, { Component } from "react";
import { observer } from "mobx-react";

import { capitalize } from "@formant/core/common/capitalize";
import { defined } from "@formant/core/common/defined";
import { IRole } from "@formant/core/model/IRole";

import { authStore, roleStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import { Avatar } from "../../common/Avatar";
import { DataTable } from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { RestrictedComponent } from "../../common/RestrictedComponent";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterDimension,
    ISimpleFilterState,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";
import * as styles from "./index.scss";

@observer
export class Roles extends Component {
    @localSettingObservable("settings-roles-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    public render() {
        const { filter } = this;
        const roles = roleStore
            .list()
            ?.filter(
                role =>
                    (role.name !== "owner" &&
                        !authStore.hasRequiredAccess(
                            "billing",
                            "administrator",
                        )) ||
                    authStore.hasRequiredAccess("billing", "administrator"),
            );
        const { routeTo } = routerStore;

        const columns: IDataTableColumn<IRole>[] = [
            {
                header: "Name",
                row: _ => (
                    <RowTitle
                        title={capitalize(_.name)}
                        icon={<Avatar initials={_.name[0]} size="small" />}
                    />
                ),
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
                hideBelow: "lg",
            },
            {
                header: "",
                row: _ => <Actions role={_} />,
            },
        ];

        const dimensions: ISimpleFilterDimension<IRole, any>[] = [];

        const filteredRoles = applySimpleFilter(
            roles || [],
            dimensions,
            (_: IRole) => _.name,
            filter,
        );

        return (
            <SettingsPage
                title="Roles"
                className={styles.users}
                extra={
                    <Help
                        text="Roles allow users to view, manage, or operate certain resources."
                        to="https://docs.formant.io/docs/resources"
                    />
                }
                actions={
                    <>
                        <RestrictedComponent
                            resource="roles"
                            requiredAccess="administrator"
                        >
                            <StandardButton to={routeTo.createRole()}>
                                Create role
                            </StandardButton>
                        </RestrictedComponent>
                    </>
                }
            >
                <StatsHeader
                    icon="access"
                    loading={!roles}
                    totalQuantity={roles?.length || 0}
                    totalLabel="Roles"
                />
                <Scrollable>
                    <DataTable
                        rows={filteredRoles || []}
                        columns={columns}
                        loading={roles === undefined}
                        noResultsText="No roles found"
                        onRowClicked={this.handleEdit}
                        className={styles.table}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private async handleEdit(role: IRole) {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editRole(
                { id: defined(role.id) },
                { editing: !role.isImmutable, fromSettings: true },
            ),
        );
    }
}
